import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
// DOC's fonts from www.doc.govt.nz, bundled with the app (no external font
// requests): Clear Sans for text, Zilla Slab for headings.
import '@fontsource/clear-sans/400.css';
import '@fontsource/clear-sans/700.css';
import '@fontsource/zilla-slab/500.css';
import '@fontsource/zilla-slab/700.css';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';

// DOC colours, read from www.doc.govt.nz (2026-11-03): the header green, the
// yellow top bar, body text and page background, plus the site's secondary
// green and link blue.
export const DOC = {
  green: '#194036',
  greenMid: '#2E5E4E',
  greenSoft: '#47665E',
  yellow: '#FFC51D',
  text: '#384246',
  background: '#F5F4F2',
  blue: '#1579B7',
};

// Page themes give the page headers and the Platform Tools / template card
// headers their gradient; all DOC greens instead of Backstage's rainbow.
const docPage = (from: string, to: string) =>
  genPageTheme({ colors: [from, to], shape: shapes.wave, options: { fontColor: '#FFFFFF' } });

const pageTheme = {
  home: docPage(DOC.green, DOC.greenSoft),
  documentation: docPage(DOC.greenSoft, DOC.green),
  tool: docPage(DOC.green, DOC.greenMid),
  service: docPage(DOC.greenMid, DOC.greenSoft),
  website: docPage(DOC.text, DOC.green),
  library: docPage(DOC.greenSoft, DOC.text),
  other: docPage(DOC.green, DOC.text),
  app: docPage(DOC.greenMid, DOC.green),
  apis: docPage(DOC.text, DOC.greenSoft),
  card: docPage(DOC.green, DOC.greenMid),
};

const BODY_FONT = '"Clear Sans", "Helvetica Neue", Arial, sans-serif';
const HEADING_FONT = '"Zilla Slab", Georgia, serif';

const typography = {
  ...defaultTypography,
  fontFamily: BODY_FONT,
  h1: { ...defaultTypography.h1, fontFamily: HEADING_FONT },
  h2: { ...defaultTypography.h2, fontFamily: HEADING_FONT },
  h3: { ...defaultTypography.h3, fontFamily: HEADING_FONT },
  h4: { ...defaultTypography.h4, fontFamily: HEADING_FONT },
  h5: { ...defaultTypography.h5, fontFamily: HEADING_FONT },
  h6: { ...defaultTypography.h6, fontFamily: HEADING_FONT },
};

// The sidebar is DOC green with the yellow selection marker in both modes, like
// the site's header.
const navigation = {
  background: DOC.green,
  indicator: DOC.yellow,
  color: '#C9D6D1',
  selectedColor: '#FFFFFF',
  navItem: { hoverBackground: DOC.greenMid },
  submenu: { background: DOC.greenMid },
};

const docLight = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: DOC.green },
    secondary: { main: DOC.yellow },
    background: { default: DOC.background, paper: '#FFFFFF' },
    text: { primary: DOC.text, secondary: '#5F6B70' },
    link: DOC.blue,
    navigation,
  },
  defaultPageTheme: 'home',
  pageTheme,
  fontFamily: BODY_FONT,
  typography,
});

const docDark = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: DOC.yellow },
    secondary: { main: '#8FB8A8' },
    link: '#8FC8EE',
    navigation,
  },
  defaultPageTheme: 'home',
  pageTheme,
  fontFamily: BODY_FONT,
  typography,
});

// Same ids as Backstage's built-in themes so they replace them and users keep
// their light/dark choice.
const lightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'DOC light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={docLight} children={children} />
      ),
    },
  },
});

const darkTheme = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'DOC dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={docDark} children={children} />
      ),
    },
  },
});

export const docThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [lightTheme, darkTheme],
});
